import { useEffect, useState } from 'react';
import { licenseService, planFeatureService, featureService } from '@kss/api';

/**
 * Result returned by {@link useFeature}.
 *
 * - `enabled`: whether the requested feature code is included in the tenant's
 *   active subscription plan. While the gate data is still loading, this is
 *   `true` (permissive) so dev environments without seeded licenses don't
 *   accidentally hide every gated UI element.
 * - `loading`: true while the license + plan-features round-trip is in flight.
 * - `source`: `'plan'` once the API has responded; `'fallback'` while loading
 *   or when the API call failed (so caller can show a tooltip if it wants).
 */
export interface FeatureGateResult {
  enabled: boolean;
  loading: boolean;
  source: 'plan' | 'fallback';
}

interface GateData {
  /** Lower-cased feature codes the active plan grants. */
  codes: Set<string>;
  /** Reason the gate failed to load, if any. */
  error?: string;
}

let cache: GateData | null = null;
let inFlight: Promise<GateData> | null = null;

async function loadGateData(): Promise<GateData> {
  if (cache) return cache;
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      // 1. Pick the active (or first available) license for the current tenant.
      //    Live API exposes LicenseStatus as int (1 = Active); legacy string
      //    payloads carried "Active". Match either.
      const licenses = await licenseService.all().catch(() => []);
      const active =
        (licenses ?? []).find((l) => l.status === 1 || String(l.status ?? '').toLowerCase() === 'active') ??
        licenses?.[0];
      // Live API field is `subscriptionPlanId`; legacy `planId` kept as fallback.
      const planId = active?.subscriptionPlanId ?? active?.planId;
      if (!planId) {
        cache = { codes: new Set(), error: 'No active license' };
        return cache;
      }
      // 2. Fetch plan features for that plan.
      const planFeatures = await planFeatureService
        .filter({ subscriptionPlanId: planId })
        .catch(() => [] as Awaited<ReturnType<typeof planFeatureService.filter>>);

      // 3. Resolve feature codes — prefer the denormalised featureCode field;
      //    if missing, look up Feature.code by featureId.
      const codes = new Set<string>();
      const missingFeatureIds: string[] = [];
      for (const pf of planFeatures ?? []) {
        if (pf.featureCode) codes.add(pf.featureCode.toLowerCase());
        else if (pf.featureId) missingFeatureIds.push(pf.featureId);
      }
      if (missingFeatureIds.length > 0) {
        const allFeatures = await featureService.all().catch(() => []);
        const byId = new Map(
          (allFeatures ?? [])
            .filter((f) => f.featureId && f.code)
            .map((f) => [f.featureId!, f.code!.toLowerCase()] as const)
        );
        for (const id of missingFeatureIds) {
          const code = byId.get(id);
          if (code) codes.add(code);
        }
      }
      cache = { codes };
      return cache;
    } catch (e) {
      cache = { codes: new Set(), error: e instanceof Error ? e.message : 'Failed' };
      return cache;
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

/** Reset the cache — call after a tenant switch or license update. */
export function refreshFeatureGate(): void {
  cache = null;
  inFlight = null;
}

/**
 * Returns whether `featureCode` is enabled for the tenant's active plan.
 *
 * While loading, defaults to `enabled: true` so callers don't accidentally
 * hide the entire UI on first render. Once the gate resolves, gated elements
 * snap to their real state.
 *
 * Usage:
 * ```tsx
 * const { enabled, loading } = useFeature('analytics.export');
 * if (!enabled && !loading) return <Tooltip>Upgrade to enable export</Tooltip>;
 * ```
 */
export function useFeature(featureCode: string): FeatureGateResult {
  const [data, setData] = useState<GateData | null>(cache);
  const [loading, setLoading] = useState(cache === null);

  useEffect(() => {
    if (cache) {
      setData(cache);
      setLoading(false);
      return;
    }
    let cancelled = false;
    loadGateData().then((d) => {
      if (cancelled) return;
      setData(d);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  if (loading || !data) {
    return { enabled: true, loading: true, source: 'fallback' };
  }
  // No license / API error → permissive fallback so dev/preview keeps working.
  if (data.error) {
    return { enabled: true, loading: false, source: 'fallback' };
  }
  return {
    enabled: data.codes.has(featureCode.toLowerCase()),
    loading: false,
    source: 'plan',
  };
}
